import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import withLayout from "../../../src/layouts/withLayout";
import { Specification } from "@Api";
import { EPageType, IDataItem, ITransformedData } from "@Types/specification";
import { useThrottleFn } from "ahooks";
import { host } from "@Services/url";
import Cookies from "js-cookie";
import { PKQuestion, setUrlParams } from "@Components/Specification/common";
import { Skeleton } from "antd";
import { g4 } from "../../utils/helper";

const MainContent = React.lazy(() => import("@Components/Compare/index"));
const Crumbs = React.lazy(() => import("@Components/Specification/crumbs"));
const RightFixedBar = React.lazy(() => import("@Components/Specification/rightFixedBar"));

const { www, c } = host;

const { fetchPkCompared, fetchPkData } = Specification;

let navTab = [
  { title: "綜合比較", key: EPageType.SUMMARY },
  { title: "規格配備比較", key: EPageType.SPEC },
  // { title: '圖片比較', href: `${c}/photoModel-compare.html?myids=${new URLSearchParams(window.location.search).get('myids') || ''}&t=ic` }
];

const App: React.FC = () => {
  const [fetchData, setFetchData] = useState<IDataItem[]>([]); // 接口数据结构
  const [transformedData, setTransformedData] = useState<ITransformedData>({ modelYearInfo: [], equipSpec: [] }); // 重组可还原的数据
  const [filteredTransformedData, setFilteredTransformedData] = useState<ITransformedData>({
    modelYearInfo: [],
    equipSpec: [],
  }); // 过滤操作后的数据
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [compareTab, setCompareTab] = useState<{ title: string; key: EPageType }>();
  const [params, setParams] = useState<{ [key: string]: any }>({});
  const isFirstRender = useRef(true);
  const [isPKQuestion, setIsPKQuestion] = useState(true);
  const [pKQuestionIsShow, setPKQuestionIsShow] = useState(true);
  const memoizedId = useMemo(() => `${compareTab?.key === EPageType.SPEC ? 'tracking-pk-compare-spec' : compareTab?.key === EPageType.SUMMARY ? 'tracking-pk-compare-summary' : ''}`, [compareTab?.key]);



  const getData = async (params) => {
    try {
      const { modelYearIds, pinnedIndex, pinnedId, emptyDefault } = params;
      setParams(params);

      setUrlParams(modelYearIds, compareTab?.key);

      const dataParams = {
        modelYearIds,
        emptyDefault,
        exModelYearId: Cookies.get("exMyids") || "",
      };

      const res: any =
        compareTab?.key === EPageType.SUMMARY
          ? await fetchPkData(dataParams)
          : compareTab?.key === EPageType.SPEC
            ? await fetchPkCompared(dataParams)
            : undefined;

      if (res && res.status === 12000) {
        // 如果广告有钉住，则车型置顶
        if (!!pinnedId && pinnedIndex !== -1) {
          let newData = res.data.items;
          const itemToMove = newData.find((item) => item.modelYearInfo.modelYearId === pinnedId);
          if (itemToMove) {
            newData = newData.filter((item) => item.modelYearInfo.modelYearId !== pinnedId);
            newData.splice(pinnedIndex, 0, itemToMove);
          }
          setFetchData(newData);
          return;
        }

        setFetchData(res.data.items);
      }
      if (res?.data?.remarketing) {
        const { autoType, brands, kinds, priceRange } = res?.data?.remarketing || {};
        const lastItem = res.data.items[res.data.items.length - 1];
        const adModelYearId = lastItem.modelYearInfo.isAds ? lastItem.modelYearInfo.modelYearId : "";

        g4("remarketing", {
          dv_price_range: priceRange,
          dv_auto_type: autoType,
          dv_kind: kinds,
          dv_brand: brands,
          ad_model_year_id: !!adModelYearId ? adModelYearId : "",
          dv_page: compareTab?.key === EPageType.SPEC ? "比較結果-規格配備比較" : "比較結果-綜合比較",
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  // 重组数据
  const transformData: any = (data: IDataItem[]) => {
    const modelYearInfos = data.map((entry) => ({
      ...entry.modelYearInfo,
      name: `${entry.modelYearInfo.brandName} ${entry.modelYearInfo.kindName} ${!!entry.modelYearInfo.year ? entry.modelYearInfo.year + "年式" : ""
        } ${entry.modelYearInfo.modelName}`,
      price: !!entry.modelYearInfo.price ? `${entry.modelYearInfo.price}萬` : "暫無報價",
    }));

    const equipSpecs: any[] = data.map((entry) => {
      // 第一层车款对应列
      return entry.equipSpec.map((spec) => {
        // 第二层对应基础行
        const newItems = spec.items.map((item) => {
          // itemss第三层对应实际values
          const newItem = { ...item, itemss: [] };

          data.forEach((innerEntry) => {
            const modelYearId = innerEntry?.modelYearInfo?.modelYearId;
            if (!modelYearId) return; // 若没有 modelYearId，跳过

            innerEntry.equipSpec.forEach((innerSpec) => {
              innerSpec.items.forEach((innerItem) => {
                if (innerItem.name === item.name && !newItem.itemss?.find((i) => i.modelYearId === modelYearId)) {
                  newItem.itemss.push({
                    type: innerItem.type,
                    unit: innerItem.unit,
                    value: innerItem.value,
                    modelYearId: modelYearId,
                  });
                }
              });
            });
          });
          return newItem;
        });

        return {
          ...spec,
          items: newItems,
        };
      });
    });

    // 数据扁平化
    const mergedEquipSpecs = !!equipSpecs.length ? [].concat(...equipSpecs[0]) : [];
    return { modelYearInfo: modelYearInfos, equipSpec: mergedEquipSpecs };
  };

  // 滚动监听层级
  const { run: debouncedHandleScroll } = useThrottleFn(
    () => {
      const scrollTop = window.scrollY;
      if (isSticky !== scrollTop > 0) {
        setIsSticky(scrollTop > 0);
      }
    },
    { wait: 30 },
  );

  const setPinAdsGetData = (params) => {
    setParams({
      ...params,
      pinnedIndex: params.pinnedIndex,
      pinnedId: params.pinnedId,
    });
  };

  useEffect(() => {
    // baseLayout 中 dimensions 会重复赋值，导致规格页中无法获取到 dimensions
    window.dimensions = null;
    const handleScroll = () => debouncedHandleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const urlIds = new URLSearchParams(window.location.search).get("myids");
    const urlTab = window.PAGE_MODEL;

    setCompareTab(
      urlTab === EPageType.SPEC
        ? { title: "規格配備比較", key: EPageType.SPEC }
        : { title: "綜合比較", key: EPageType.SUMMARY },
    );

    getData({
      modelYearIds: urlIds || "",
      emptyDefault: !!urlIds ? "" : 1,
    });
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // 跳过首次渲染
    }
    const urlIds = new URLSearchParams(window.location.search).get("myids");

    getData({
      ...params,
      modelYearIds: urlIds || "",
    });
    setPKQuestionIsShow(true);
  }, [compareTab]);

  // 获取接口数据格式
  useEffect(() => {
    if (fetchData.length > 0) {
      const $container: any = document.getElementById("container");
      $container.style.display = "grid";

      setTransformedData(transformData(fetchData));
    }
  }, [fetchData]);

  return (
    <div
      className={`min-h-screen bg-[#F8F9FC] flex rounded-tl-4 pt-8 sticky z-10 flex-col ${isSticky ? "z-[1000]" : ""}`}
    >
      {compareTab?.key === EPageType.SPEC && <div id={`tracking-ComparePageRouteSpec`} />}
      {compareTab?.key === EPageType.SUMMARY && <div id={`tracking-ComparePageRouteSummary`} />}
      {/* 面包屑 */}
      <div className='h-42 bg-[#F8F9FC] w-full'>
        <Suspense fallback={<Skeleton active={false} />}>
          <Crumbs
            sticky={true}
            stickyLeft={24}
            items={[{ label: "首頁", url: www }, { label: "比較" }, { label: compareTab?.title || "" }]}
          />
        </Suspense>
      </div>

      <div className='flex items-center justify-start w-full h-[44px] rounded-tl-4 rounded-bl-4 mb-14 bg-white'>
        <i className='w-24 h-full bg-[#F8F9FC] sticky left-0' />
        <div id='tracking-ComparePageRouteNav' className='sticky left-[24px] flex h-full bg-[#fff]'>
          {navTab.map((item, index) => (
            <div
              key={index}
              id='tracking-NavItem'
              className={`${compareTab?.key === item.key ? "text-[#3264FF]" : "text-[#1D2029]"
                } flex h-full relative items-center justify-center text-16 text-center px-20 cursor-pointer font-[600]`}
              onClick={() => {
                setPKQuestionIsShow(false);
                setCompareTab(item);
              }}
            >
              {item.title}
              {compareTab?.key === item.key && <i className='w-34 h-4 bg-[#3264FF] rounded-4 absolute bottom-0' />}
            </div>
          ))}
        </div>
      </div>

      {/* PK比較調研 */}
      {isPKQuestion && pKQuestionIsShow && (
        <PKQuestion
          onClose={() => setIsPKQuestion(false)}
          trackingId={memoizedId}
        />
      )}

      {/* 主内容 */}
      <Suspense fallback={<></>}>
        <MainContent
          fetchData={fetchData}
          transformedData={transformedData}
          filteredTransformedData={filteredTransformedData}
          setPinAdsGetData={setPinAdsGetData}
          getData={getData}
          pageType={compareTab?.key || EPageType.SPEC}
          setTransformedData={setTransformedData}
          setFilteredTransformedData={setFilteredTransformedData}
        />
      </Suspense>

      {/* 右侧置顶固定栏 */}
      <Suspense fallback={<Skeleton active={false} />}>
        <RightFixedBar scrollTopNum={130} />
      </Suspense>
    </div>
  );
};

withLayout({
  cmp: <App />,
  params: {
    singlePageNav: true,
    webPageNav: false,
    nav: "",
    footer: "Footer",
  },
});
